import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, addDoc, getDocs, updateDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { PFISymbol, BrandGradient } from '../components/common/PFILogo';
import { formatProjectName } from '../utils/projectUtils';
import { getTaskCategories, formatCategoryForDisplay } from '../lib/taskCategories';
import JobSheetModal from '../components/JobSheetModal';
import SupplierInvitationPromptModal from '../components/SupplierInvitationPromptModal';
import QuickTaskRatingModal from '../components/QuickTaskRatingModal';
import { 
  ArrowLeftIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function ProjectTasksPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [assigningTask, setAssigningTask] = useState(null);
  const [sendInvitationsImmediately, setSendInvitationsImmediately] = useState(true); // Default to immediate sending
  const [jobSheetTask, setJobSheetTask] = useState(null);
  const [taskCategories, setTaskCategories] = useState([]);
  const [showSupplierPrompt, setShowSupplierPrompt] = useState(false);
  const [createdTask, setCreatedTask] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [taskToRate, setTaskToRate] = useState(null);
  const [componentPhotos, setComponentPhotos] = useState({});
  const [newTask, setNewTask] = useState({
    name: '',
    description: 'Provide quote based on product description.',
    category: 'upholstery',
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // Default 5 days from now
    assignedSuppliers: [],
    workflowType: 'quote', // 'quote', 'jobsheet', 'directQuote'
    directQuoteSupplier: '', // For direct order workflow
    requiresQuote: true, // Keep for backward compatibility
    componentId: null // Link task to specific component
  });


  useEffect(() => {
    loadData();
  }, [id]);

  // Helper function to get supplier name for job sheet tasks
  const getSupplierNameForTask = (task) => {
    // All new job sheets will have assignedSupplierName
    if (task.assignedSupplierName) {
      return task.assignedSupplierName;
    }
    
    // For testing phase - show generic name for any missing data
    return 'supplier';
  };
  
  // Helper function to calculate due date based on category
  const calculateDueDateFromCategory = (categoryName) => {
    const category = taskCategories.find(cat => 
      typeof cat === 'string' ? cat === categoryName : cat.name === categoryName
    );
    
    // Get dueDays from category or default to 5 days
    const dueDays = category?.dueDays || 5;
    
    // Calculate due date from today
    return new Date(Date.now() + dueDays * 24 * 60 * 60 * 1000);
  };
  
  // Handle category change and update due date
  const handleCategoryChange = (categoryName) => {
    const newDueDate = calculateDueDateFromCategory(categoryName);
    setNewTask(prev => ({ 
      ...prev, 
      category: categoryName,
      dueDate: newDueDate
    }));
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project, tasks, suppliers, and categories in parallel
      const [projectDoc, tasksSnapshot, suppliersSnapshot, categories] = await Promise.all([
        getDoc(doc(db, 'projects', id)),
        getDocs(collection(db, 'projects', id, 'tasks')),
        getDocs(collection(db, 'suppliers')),
        getTaskCategories()
      ]);

      // Set project
      if (projectDoc.exists()) {
        const projectData = { id: projectDoc.id, ...projectDoc.data() };
        console.log('Project loaded successfully:', projectData.name, 'ID:', projectData.id);
        setProject(projectData);
      } else {
        console.error('Project document does not exist for ID:', id);
        setProject(null);
      }

      // Set tasks
      const tasksData = tasksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);

      // Set suppliers
      const suppliersData = suppliersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSuppliers(suppliersData);

      // Set task categories
      setTaskCategories(categories);
      
      // Set default category for new tasks and calculate initial due date
      if (categories.length > 0) {
        const firstCategory = typeof categories[0] === 'string' ? categories[0] : categories[0].name;
        const firstCategoryDueDays = categories[0]?.dueDays || 5;
        const calculatedDueDate = new Date(Date.now() + firstCategoryDueDays * 24 * 60 * 60 * 1000);
        
        setNewTask(prev => ({ 
          ...prev, 
          category: firstCategory,
          dueDate: calculatedDueDate
        }));
      }

      // Load component photos if project has components
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        if (projectData.components && projectData.components.length > 0) {
          try {
            const componentPhotosQuery = query(
              collection(db, 'componentPhotos'),
              where('projectId', '==', id)
            );
            const componentPhotosSnapshot = await getDocs(componentPhotosQuery);
            let componentPhotosData = componentPhotosSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Group component photos by componentId
            const groupedComponentPhotos = {};
            componentPhotosData.forEach(photo => {
              const componentId = photo.componentId;
              if (!groupedComponentPhotos[componentId]) {
                groupedComponentPhotos[componentId] = [];
              }
              groupedComponentPhotos[componentId].push(photo);
            });
            
            console.log('Loaded component photos for project tasks:', groupedComponentPhotos);
            setComponentPhotos(groupedComponentPhotos);
          } catch (photoError) {
            console.error('Error loading component photos:', photoError);
            setComponentPhotos({});
          }
        }
      }

      // No backwards compatibility - all new job sheets will have supplier info directly on tasks

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Validation for direct order
    if (newTask.workflowType === 'directQuote' && !newTask.directQuoteSupplier.trim()) {
      alert('Please enter supplier details for direct order tasks.');
      return;
    }
    
    try {
      // Determine initial status based on workflow type
      let initialStatus = 'pending';
      let taskData = { ...newTask };
      
      if (newTask.workflowType === 'jobsheet') {
        initialStatus = 'job_sheet_sent';
        taskData.requiresQuote = false;
      } else if (newTask.workflowType === 'directQuote') {
        initialStatus = 'completed'; // Direct orders are considered complete immediately
        taskData.requiresQuote = false;
        taskData.directQuoteCompleted = true;
      } else {
        taskData.requiresQuote = true;
      }
      
      const taskRef = await addDoc(collection(db, 'projects', id, 'tasks'), {
        ...taskData,
        componentId: taskData.componentId || null, // Include component reference
        status: initialStatus,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Store created task data for the prompt modal
      const taskForPrompt = {
        id: taskRef.id,
        ...taskData,
        status: initialStatus
      };
      
      // Reset form with proper due date based on category
      const defaultCategory = typeof taskCategories[0] === 'string' ? taskCategories[0] : taskCategories[0]?.name || 'upholstery';
      const defaultDueDays = taskCategories[0]?.dueDays || 5;
      const newDueDate = new Date(Date.now() + defaultDueDays * 24 * 60 * 60 * 1000);
      
      setNewTask({
        name: '',
        description: 'Provide quote based on product description.',
        category: defaultCategory,
        dueDate: newDueDate,
        assignedSuppliers: [],
        workflowType: 'quote',
        directQuoteSupplier: '',
        requiresQuote: true
      });
      setShowCreateForm(false);
      
      // Show supplier invitation prompt for quote tasks only
      if (taskData.workflowType === 'quote' && taskData.requiresQuote) {
        setCreatedTask(taskForPrompt);
        setShowSupplierPrompt(true);
      }
      
      loadData();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      // Check if task is being marked as completed and doesn't already have a rating
      const task = tasks.find(t => t.id === taskId);
      if (updates.status === 'completed' && 
          task && 
          !task.hasQuickRating &&
          (task.assignedSupplierName || task.directQuoteSupplier)) {
        // Show rating modal after update
        setTaskToRate(task);
        setShowRatingModal(true);
      }
      
      await updateDoc(doc(db, 'projects', id, 'tasks', taskId), {
        ...updates,
        updatedAt: new Date()
      });
      loadData();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      // Check if task has active (non-archived) quotes
      const quotesSnapshot = await getDocs(collection(db, 'projects', id, 'tasks', taskId, 'quotes'));
      const activeQuotes = quotesSnapshot.docs.filter(doc => !doc.data().archived);
      
      if (activeQuotes.length > 0) {
        const quoteDetails = activeQuotes.map(doc => {
          const data = doc.data();
          return `- ${data.supplierName || 'Unknown'}: ${data.status || 'unknown status'} (${data.amount ? '$' + data.amount : 'no amount'})`;
        }).join('\n');
        
        alert(`⚠️ Cannot delete task!\n\nThis task has ${activeQuotes.length} active quote(s):\n\n${quoteDetails}\n\nPlease remove all suppliers or use "Clear All Invitations" to archive all quotes before deleting the task.`);
        return;
      }

      // Check if task has invited suppliers
      const task = tasks.find(t => t.id === taskId);
      if (task?.invitedSuppliers?.length > 0) {
        const confirmWithSuppliers = window.confirm(
          `⚠️ This task has ${task.invitedSuppliers.length} invited supplier(s).\n\nDeleting this task will remove all supplier invitations.\n\nAre you sure you want to delete this task?`
        );
        if (!confirmWithSuppliers) return;
      } else {
        // Standard confirmation for tasks without suppliers
        if (!window.confirm('Are you sure you want to delete this task?')) {
          return;
        }
      }

      await deleteDoc(doc(db, 'projects', id, 'tasks', taskId));
      loadData();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task: ' + error.message);
    }
  };

  const handleRemoveInvitedSupplier = async (taskId, supplierIndex) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task || !task.invitedSuppliers) return;

      const supplierToRemove = task.invitedSuppliers[supplierIndex];
      if (!supplierToRemove) return;

      // Remove supplier from task's invitedSuppliers array
      const updatedSuppliers = [...task.invitedSuppliers];
      updatedSuppliers.splice(supplierIndex, 1);

      await updateDoc(doc(db, 'projects', id, 'tasks', taskId), {
        invitedSuppliers: updatedSuppliers,
        updatedAt: new Date()
      });

      // Archive any existing quotes from this supplier for this task
      const quotesQuery = query(
        collection(db, 'projects', id, 'tasks', taskId, 'quotes'),
        where('supplierId', '==', supplierToRemove.id)
      );
      const quotesSnapshot = await getDocs(quotesQuery);
      
      const archiveQuotePromises = quotesSnapshot.docs.map(quoteDoc => 
        updateDoc(quoteDoc.ref, {
          archived: true,
          archivedAt: new Date(),
          archivedReason: 'Supplier removed from task',
          updatedAt: new Date()
        })
      );
      await Promise.all(archiveQuotePromises);

      // Archive any quote requests for this supplier/task combination
      const quoteRequestsQuery = query(
        collection(db, 'quoteRequests'),
        where('supplierId', '==', supplierToRemove.id),
        where('taskId', '==', taskId),
        where('projectId', '==', id)
      );
      const quoteRequestsSnapshot = await getDocs(quoteRequestsQuery);
      
      const archiveRequestPromises = quoteRequestsSnapshot.docs.map(requestDoc => 
        updateDoc(requestDoc.ref, {
          archived: true,
          archivedAt: new Date(),
          archivedReason: 'Supplier removed from task',
          updatedAt: new Date()
        })
      );
      await Promise.all(archiveRequestPromises);

      console.log(`Removed supplier ${supplierToRemove.name} and archived ${quotesSnapshot.docs.length} quotes and ${quoteRequestsSnapshot.docs.length} quote requests`);
      
      loadData();
    } catch (error) {
      console.error('Error removing invited supplier:', error);
      alert('Failed to remove supplier: ' + error.message);
    }
  };

  const handleClearAllInvitations = async (taskId) => {
    if (window.confirm('Are you sure you want to clear all supplier invitations for this task? This will archive all quotes and quote requests for future reference.')) {
      try {
        // Clear suppliers from task
        await updateDoc(doc(db, 'projects', id, 'tasks', taskId), {
          invitedSuppliers: [],
          updatedAt: new Date()
        });

        // Archive all quotes for this task
        const quotesSnapshot = await getDocs(collection(db, 'projects', id, 'tasks', taskId, 'quotes'));
        const archiveQuotePromises = quotesSnapshot.docs.map(quoteDoc => 
          updateDoc(quoteDoc.ref, {
            archived: true,
            archivedAt: new Date(),
            archivedReason: 'All invitations cleared for task',
            updatedAt: new Date()
          })
        );
        await Promise.all(archiveQuotePromises);

        // Archive all quote requests for this task
        const quoteRequestsQuery = query(
          collection(db, 'quoteRequests'),
          where('taskId', '==', taskId),
          where('projectId', '==', id)
        );
        const quoteRequestsSnapshot = await getDocs(quoteRequestsQuery);
        const archiveRequestPromises = quoteRequestsSnapshot.docs.map(requestDoc => 
          updateDoc(requestDoc.ref, {
            archived: true,
            archivedAt: new Date(),
            archivedReason: 'All invitations cleared for task',
            updatedAt: new Date()
          })
        );
        await Promise.all(archiveRequestPromises);

        console.log(`Cleared all invitations and archived ${quotesSnapshot.docs.length} quotes and ${quoteRequestsSnapshot.docs.length} quote requests for task ${taskId}`);
        
        loadData();
      } catch (error) {
        console.error('Error clearing invitations:', error);
        alert('Failed to clear invitations: ' + error.message);
      }
    }
  };

  const handleAssignSuppliers = async (taskId, supplierIds, sendImmediately = false) => {
    try {
      const taskRef = doc(db, 'projects', id, 'tasks', taskId);
      
      // Get current task data to preserve existing invited suppliers
      const taskDoc = await getDoc(taskRef);
      const currentTask = taskDoc.exists() ? taskDoc.data() : {};
      const existingInvitedSuppliers = currentTask.invitedSuppliers || [];
      
      // Create map of new supplier selections
      const newSupplierIds = new Set(supplierIds);
      
      // Keep existing suppliers that aren't being modified and add new ones
      const updatedInvitedSuppliers = [...existingInvitedSuppliers];
      
      // Add new suppliers that aren't already invited
      supplierIds.forEach(supplierId => {
        const alreadyExists = existingInvitedSuppliers.some(s => s.id === supplierId);
        if (!alreadyExists) {
          const supplier = suppliers.find(s => s.id === supplierId);
          updatedInvitedSuppliers.push({
            id: supplierId,
            name: supplier?.name || 'Unknown',
            email: supplier?.email || '',
            phone: supplier?.phone || '',
            category: supplier?.category || '',
            status: 'invited',
            invitedAt: new Date(),
            invitationSent: false,
            invitationSentAt: null,
            quoteStatus: null,
            quoteAmount: null,
            quoteSubmittedAt: null
          });
        }
      });

      await updateDoc(taskRef, {
        invitedSuppliers: updatedInvitedSuppliers,
        updatedAt: new Date()
      });
      
      setAssigningTask(null);
      
      // If immediate sending is enabled, send invitations automatically
      if (sendImmediately) {
        // Get the updated task to send invitations
        const updatedTaskDoc = await getDoc(taskRef);
        if (updatedTaskDoc.exists()) {
          const taskWithSuppliers = { id: updatedTaskDoc.id, ...updatedTaskDoc.data() };
          await handleSendInvitations(taskWithSuppliers);
        }
      }
      
      loadData(); // Reload tasks
    } catch (error) {
      console.error('Error assigning suppliers:', error);
      alert('Failed to assign suppliers: ' + error.message);
    }
  };

  // Supplier invitation prompt modal handlers
  const handleInviteSuppliers = (task) => {
    setAssigningTask(task);
    setShowSupplierPrompt(false);
  };

  const handleViewTask = (task) => {
    // Could navigate to task details or just close
    setShowSupplierPrompt(false);
  };

  const handleSendInvitations = async (task) => {
    try {
      // Ensure project data is loaded
      if (!project) {
        console.error('❌ Project data not loaded. Current project state:', project);
        console.error('❌ Project ID from URL:', id);
        alert('⚠️ Project data not loaded. Please refresh the page and try again.');
        return;
      }
      
      console.log('✅ Project data available for invitations:', project.name, 'ID:', project.id);

      const pendingInvites = task.invitedSuppliers.filter(s => !s.invitationSent);
      
      if (pendingInvites.length === 0) {
        alert('All suppliers have already been notified');
        return;
      }
      
      console.log('Sending invitations for project:', project.name, 'task:', task.name);

      // Load photos for this project and task/component
      let generalPhotos = [];
      let componentPhotos = [];
      let fabricImages = [];
      
      try {
        // If task is linked to a component, load component-specific data
        // Otherwise, load general project photos
        if (task.componentId) {
          // Find the component details (handle both string and number IDs)
          const taskComponent = project.components?.find(comp => 
            comp.id === task.componentId || comp.id === String(task.componentId) || comp.id === Number(task.componentId)
          );
          
          if (taskComponent) {
            // Load component-specific photos
            // First try using the photo URLs directly from the component's taxonomyData
            console.log(`🧪 Checking for component photos in taxonomyData:`, !!taskComponent.taxonomyData?.componentPhotos?.imageUrls);
            console.log(`🧪 Component imageUrls length:`, taskComponent.taxonomyData?.componentPhotos?.imageUrls?.length);
            if (taskComponent.taxonomyData?.componentPhotos?.imageUrls?.length > 0) {
              componentPhotos = taskComponent.taxonomyData.componentPhotos.imageUrls.map((url, index) => ({
                id: `component-${task.componentId}-${index}`,
                url: url,
                filename: `component-photo-${index + 1}`,
                originalName: `component-photo-${index + 1}`,
                componentName: taskComponent.name,
                type: 'component',
                caption: `${taskComponent.name} photo`
              }));
              
              console.log(`✅ Loaded ${componentPhotos.length} component photos from taxonomyData for ${taskComponent.name}`);
            }
            
            // Also load fabric photos from taxonomyData if they exist
            let fabricPhotosFromTaxonomy = [];
            console.log(`🧪 Checking for fabric photos in taxonomyData:`, !!taskComponent.taxonomyData?.fabric?.imageUrls);
            console.log(`🧪 Fabric imageUrls length:`, taskComponent.taxonomyData?.fabric?.imageUrls?.length);
            if (taskComponent.taxonomyData?.fabric?.imageUrls?.length > 0) {
              fabricPhotosFromTaxonomy = taskComponent.taxonomyData.fabric.imageUrls.map((url, index) => ({
                id: `fabric-${task.componentId}-${index}`,
                url: url,
                filename: `fabric-photo-${index + 1}`,
                originalName: `fabric-photo-${index + 1}`,
                componentName: taskComponent.name,
                type: 'fabric',
                description: taskComponent.taxonomyData.fabric.description || 'Fabric selection',
                caption: `${taskComponent.name} fabric`
              }));
              
              console.log(`✅ Loaded ${fabricPhotosFromTaxonomy.length} fabric photos from taxonomyData for ${taskComponent.name}`);
            }
            
            if (componentPhotos.length === 0) {
              // Fallback: Try database queries with different componentId formats
              const componentPhotosQuery1 = query(
                collection(db, 'componentPhotos'),
                where('projectId', '==', id),
                where('componentId', '==', task.componentId)
              );
              const componentPhotosQuery2 = query(
                collection(db, 'componentPhotos'),
                where('projectId', '==', id),
                where('componentId', '==', Number(task.componentId))
              );
              
              // Try both queries and combine results
              const [snapshot1, snapshot2] = await Promise.all([
                getDocs(componentPhotosQuery1).catch(() => ({ docs: [] })),
                getDocs(componentPhotosQuery2).catch(() => ({ docs: [] }))
              ]);
              
              // Combine and deduplicate by id
              const allDocs = [...snapshot1.docs, ...snapshot2.docs];
              const uniqueDocs = allDocs.filter((doc, index, arr) => 
                arr.findIndex(d => d.id === doc.id) === index
              );
              
              componentPhotos = uniqueDocs.map(doc => ({
                id: doc.id,
                url: doc.data().url,
                filename: doc.data().filename,
                originalName: doc.data().originalName,
                componentName: taskComponent.name,
                type: 'component',
                caption: `${taskComponent.name} photo`
              }));
              
              console.log(`📋 Fallback: Loaded ${componentPhotos.length} component photos from database for ${taskComponent.name}`);
            }
            
            // Use only fabric photos from taxonomyData (no database lookup needed)
            fabricImages = fabricPhotosFromTaxonomy;
            console.log(`Using ${fabricImages.length} fabric photos from taxonomyData only`);
            
            console.log(`Task is linked to component "${taskComponent.name}" - loaded ${componentPhotos.length} component photos`);
          }
        } else {
          // Task not linked to specific component - load general photos
          const generalPhotosQuery = query(
            collection(db, 'projectPhotos'),
            where('projectId', '==', id)
          );
          const generalPhotosSnapshot = await getDocs(generalPhotosQuery);
          generalPhotos = generalPhotosSnapshot.docs.map(doc => ({
            id: doc.id,
            url: doc.data().url,
            filename: doc.data().filename,
            originalName: doc.data().originalName,
            type: 'general',
            caption: 'General project photo'
          }));
          
          // For general tasks, no fabric images from taxonomyData (component-specific only)
          fabricImages = [];
          console.log('General task - no fabric images from taxonomyData');
          
          console.log('Task not linked to specific component - loading general photos');
        }
        
        console.log(`🔍 Final photo counts before quote creation:`);
        console.log(`  - generalPhotos: ${generalPhotos.length}`);
        console.log(`  - componentPhotos: ${componentPhotos.length}`); 
        console.log(`  - fabricImages: ${fabricImages.length}`);
        console.log(`Loaded ${generalPhotos.length} general photos and ${componentPhotos.length} component photos for task: ${task.name}`);
      } catch (photoError) {
        console.error('Error loading photos for quote request:', photoError);
        // Continue without photos if loading fails
      }

      // Create quote requests for each supplier
      const quoteRequests = [];
      
      for (const supplier of pendingInvites) {
        // Generate a unique token for this quote request
        const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
        
        // Determine what product specs to include based on component linkage
        let productSpecs = null;
        let projectDescription = '';
        
        if (task.componentId) {
          // Task is linked to a component - include only component-specific info
          // Handle both string and number component IDs
          const taskComponent = project.components?.find(comp => 
            comp.id === task.componentId || comp.id === String(task.componentId) || comp.id === Number(task.componentId)
          );
          
          console.log(`🔍 Looking for component ID: ${task.componentId} (type: ${typeof task.componentId})`);
          console.log(`🔍 Available component IDs:`, project.components?.map(c => `${c.id} (${typeof c.id})`));
          console.log(`🔍 Found component:`, taskComponent?.name);
          console.log(`🔍 Component has taxonomyData:`, !!taskComponent?.taxonomyData);
          
          if (taskComponent) {
            console.log(`🔍 About to load component photos for: ${taskComponent.name}`);
          }
          
          if (taskComponent) {
            // Include ALL component-specific taxonomy data
            productSpecs = taskComponent.taxonomyData ? {
              product: taskComponent.taxonomyData.product?.label || null,
              category: taskComponent.taxonomyData.category?.label || null,
              subcategory: taskComponent.taxonomyData.subcategory?.label || null,
              features: taskComponent.taxonomyData.features?.map(f => 
                typeof f === 'string' ? f : (f.label || f)
              ) || [],
              additionals: taskComponent.taxonomyData.additionals?.map(a => 
                typeof a === 'string' ? a : (a.label || a)
              ) || [],
              dimensions: taskComponent.taxonomyData.dimensions || {},
              fabric: taskComponent.taxonomyData.fabric ? {
                description: taskComponent.taxonomyData.fabric.description || '',
                imageCount: taskComponent.taxonomyData.fabric.imageCount || 0,
                imageUrls: taskComponent.taxonomyData.fabric.imageUrls || [],
                // Include all fabric data
                ...taskComponent.taxonomyData.fabric
              } : null,
              materials: taskComponent.taxonomyData.materials?.map(m => 
                typeof m === 'string' ? m : (m.label || m)
              ) || [],
              componentPhotos: taskComponent.taxonomyData.componentPhotos || [],
              componentName: taskComponent.name,
              componentNotes: taskComponent.notes || null,
              description: taskComponent.taxonomyData.description || '',
              generatedDescription: taskComponent.taxonomyData.generatedDescription || ''
            } : {
              // Even if no taxonomyData, include basic component info
              componentName: taskComponent.name,
              componentNotes: taskComponent.notes || null
            };
            
            // Use comprehensive component description
            projectDescription = taskComponent.taxonomyData?.generatedDescription || 
                               taskComponent.taxonomyData?.description || 
                               taskComponent.notes || 
                               taskComponent.description || 
                               `Component: ${taskComponent.name}`;
          } else {
            console.log(`❌ Component with ID ${task.componentId} not found in project components`);
            console.log(`📋 Available components:`, project.components?.map(c => ({id: c.id, name: c.name})));
          }
        } else {
          // Task not linked to component - include general project info
          productSpecs = project.smartQuoteData ? {
            product: project.smartQuoteData.product?.label || null,
            category: project.smartQuoteData.category?.label || null,
            subcategory: project.smartQuoteData.subcategory?.label || null,
            features: project.smartQuoteData.features?.map(f => f.label) || [],
            dimensions: project.smartQuoteData.dimensions || {},
            fabric: project.smartQuoteData.fabric ? {
              description: project.smartQuoteData.fabric.description || '',
              imageCount: project.smartQuoteData.fabric.imageCount || 0
            } : null
          } : null;
          
          projectDescription = project.projectDescription || '';
        }
        
        // Create quote request document with filtered details
        const quoteRequest = {
          projectId: id,
          projectName: project.name,  // Now we know project exists
          taskId: task.id,
          taskName: task.name,
          taskDescription: task.description || '',
          taskCategory: task.category || 'general',
          componentId: task.componentId || null,  // Include component reference
          supplierId: supplier.id,
          supplierName: supplier.name,
          supplierEmail: supplier.email,
          token: token,
          status: 'pending',
          invitedAt: serverTimestamp(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          budget: task.budget || null,
          quoteAmount: null,
          quotedAt: null,
          notes: null,
          // Include filtered product specifications
          productSpecs: productSpecs,
          // Include filtered project/component description
          projectDescription: projectDescription,
          // Include photos (only relevant photos based on component linkage)
          photos: {
            general: generalPhotos,
            component: componentPhotos,
            fabric: fabricImages,
            totalCount: generalPhotos.length + componentPhotos.length + fabricImages.length
          }
        };
        
        const docRef = await addDoc(collection(db, 'quoteRequests'), quoteRequest);
        quoteRequests.push({ ...quoteRequest, id: docRef.id });
      }

      // Update task to mark suppliers as notified
      const updatedSuppliers = task.invitedSuppliers.map(supplier => {
        if (!supplier.invitationSent) {
          return {
            ...supplier,
            invitationSent: true,
            invitationSentAt: new Date()
          };
        }
        return supplier;
      });

      await updateDoc(doc(db, 'projects', id, 'tasks', task.id), {
        invitedSuppliers: updatedSuppliers,
        updatedAt: new Date()
      });

      // Create notification records (deduplicate by supplier ID)
      const uniqueSupplierIds = [...new Set(quoteRequests.map(r => r.supplierId))];
      const uniqueRequests = uniqueSupplierIds.map(supplierId => 
        quoteRequests.find(r => r.supplierId === supplierId)
      );
      
      for (const request of uniqueRequests) {
        const supplier = pendingInvites.find(s => s.id === request.supplierId);
        const originalSupplier = suppliers.find(s => s.id === request.supplierId);
        
        console.log('🔍 Debug notification data:', {
          supplierEmail: request.supplierEmail,
          originalSupplierEmail: originalSupplier?.email,
          finalEmail: originalSupplier?.email || request.supplierEmail,
          originalSupplierPhone: originalSupplier?.phone,
          supplierPhone: supplier?.phone,
          finalPhone: originalSupplier?.phone || supplier?.phone || null
        });

        const finalEmail = originalSupplier?.email || request.supplierEmail;
        
        if (!finalEmail || finalEmail.trim() === '') {
          console.error('❌ No email address found for supplier:', request.supplierName);
          alert(`Warning: No email address found for supplier ${request.supplierName}. They will only receive SMS notification.`);
        } else {
          console.log('✅ Email will be sent to:', finalEmail);
        }
        
        await addDoc(collection(db, 'notifications'), {
          type: 'quote-invitation-sent',
          recipientId: request.supplierId,
          recipientEmail: originalSupplier?.email || request.supplierEmail,
          recipientPhone: originalSupplier?.phone || supplier?.phone || null,
          recipientName: request.supplierName,
          projectId: id,
          projectName: project.name,  // Use actual project name
          taskId: task.id,
          taskName: task.name,
          taskDescription: task.description || '',
          taskCategory: task.category || 'general',
          budget: task.budget || null,
          message: `You've been invited to quote on: ${task.name}`,
          magicLink: `${window.location.origin}/quote/${request.token}`,
          status: 'pending',
          createdAt: serverTimestamp()
        });

        // Also create a communication record for tracking in the comms section
        await addDoc(collection(db, 'communications'), {
          projectId: id,
          taskId: task.id,
          threadId: null, // This is a new thread
          messageType: 'pfi-supplier',
          senderId: 'pfi-system',
          senderType: 'pfi',
          senderName: 'Pink Flamingo Interiors',
          senderEmail: 'team@pinkflamingointeriors.com.au',
          recipientIds: [request.supplierId],
          recipientTypes: ['supplier'],
          recipientNames: [request.supplierName],
          subject: `Quote Request: ${task.name}`,
          message: `You've been invited to provide a quote for "${task.name}" in project "${project.name}". 
          
Task Description: ${task.description || 'Provide quote based on product description.'}
Category: ${task.category || 'General'}
${task.budget ? `Budget: $${task.budget}` : ''}

Please click the link in your email to submit your quote online.`,
          status: 'sent',
          readBy: ['pfi-system'],
          priority: 'high',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          relatedTo: {
            type: 'quote-request',
            referenceId: request.token
          },
          replyCount: 0
        });
      }

      alert(`✅ Invitations sent to ${pendingInvites.length} supplier(s)!\n\nThey will receive email notifications with magic links to submit their quotes.`);
      loadData();
      
    } catch (error) {
      console.error('Error sending invitations:', error);
      alert('Failed to send invitations: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tasks...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Link
              to={`/projects/${id}`}
              className="inline-flex items-center px-4 py-2 bg-black bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm border border-white border-opacity-30"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Project
            </Link>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-6 py-3 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors shadow-lg"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Task
            </button>
          </div>
          
          <div className="flex items-start">
            <CheckCircleIcon size="lg" className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Project Tasks</h1>
              <p className="text-pink-100 text-xl">
                {formatProjectName(project) || 'Project'} - Task Management
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  {tasks.filter(t => t.status === 'completed').length} / {tasks.length} Completed
                </div>
                <div className="flex items-center">
                  <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                  Total Budget: {formatCurrency(tasks.reduce((sum, task) => sum + (task.budget || 0), 0))}
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Create Task Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Task</h2>
                <form onSubmit={handleCreateTask} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Task Name</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={newTask.name}
                      onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                      placeholder="e.g., Custom sofa upholstery"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      placeholder="Detailed description of the task requirements..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="taskType"
                            checked={newTask.workflowType === 'quote'}
                            onChange={() => setNewTask({ ...newTask, workflowType: 'quote', requiresQuote: true })}
                            className="w-4 h-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Quote Required</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="taskType"
                            checked={newTask.workflowType === 'jobsheet'}
                            onChange={() => setNewTask({ ...newTask, workflowType: 'jobsheet', requiresQuote: false })}
                            className="w-4 h-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Job Sheet</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="taskType"
                            checked={newTask.workflowType === 'directQuote'}
                            onChange={() => setNewTask({ ...newTask, workflowType: 'directQuote', requiresQuote: false })}
                            className="w-4 h-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Direct Order</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        {newTask.workflowType === 'quote' 
                          ? "Task will go through quotation process with multiple suppliers"
                          : newTask.workflowType === 'jobsheet'
                          ? "Task will be sent directly to supplier as job sheet for acknowledgment"
                          : "Direct order from specified supplier - no quotes or job sheets required"
                        }
                      </p>

                      {/* Direct Order Supplier Field */}
                      {newTask.workflowType === 'directQuote' && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Supplier Details
                          </label>
                          <textarea
                            rows={3}
                            placeholder="Enter supplier name and any relevant details (e.g., contact info, specific product/service details)..."
                            value={newTask.directQuoteSupplier}
                            onChange={(e) => setNewTask({ ...newTask, directQuoteSupplier: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This information will be saved with the task for reference. No communication will be sent through the platform.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Component Selection - Only show if project has components */}
                  {project?.components && project.components.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Component (Optional)
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newTask.componentId || ''}
                        onChange={(e) => setNewTask({ ...newTask, componentId: e.target.value || null })}
                      >
                        <option value="">No specific component - General task</option>
                        {project.components.map((component) => (
                          <option key={component.id} value={component.id}>
                            {component.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Link this task to a specific component. Suppliers will only see information relevant to this component.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newTask.category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                      >
                        {taskCategories.map(category => {
                          const categoryName = typeof category === 'string' ? category : category.name;
                          const dueDays = category?.dueDays || 5;
                          return (
                            <option key={categoryName} value={categoryName}>
                              {formatCategoryForDisplay(categoryName)} ({dueDays} days)
                            </option>
                          );
                        })}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Due date will be automatically set based on category settings
                      </p>
                    </div>

                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {newTask.workflowType === 'quote' 
                          ? "Due Date for Quote" 
                          : newTask.workflowType === 'jobsheet' 
                          ? "Due Date for Job Completion" 
                          : "Due Date for Direct Order"}
                      </label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newTask.dueDate.toISOString().split('T')[0]}
                        onChange={(e) => setNewTask({ ...newTask, dueDate: new Date(e.target.value) })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {newTask.workflowType === 'quote' 
                          ? "When quotes should be received by" 
                          : newTask.workflowType === 'jobsheet' 
                          ? "When the job should be completed by" 
                          : "When the order should be delivered by"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <button
                      type="submit"
                      className="px-8 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                    >
                      Create Task
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tasks.map((task) => (
            <div key={task.id} className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6 pfi-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2 space-x-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {formatCategoryForDisplay(task.category)}
                    </span>
                    {task.hasQuickRating && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 flex items-center">
                        ★ {task.quickRating}/5
                      </span>
                    )}
                    {task.pendingDetailedReview && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        Review Later
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{task.name}</h3>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingTask(task)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {task.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{task.description}</p>
              )}

              {/* Component Details Section */}
              {(() => {
                // Find matching component for this task
                const matchingComponent = project.components?.find(comp => 
                  comp.name === task.name || comp.category === task.category
                );
                
                if (!matchingComponent) return null;
                
                return (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-800">Component Details</h4>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                        {matchingComponent.category}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-xs">
                      {/* Expected Completion Date */}
                      {matchingComponent.expectedCompletionDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Expected completion:</span>
                          <span className="text-gray-800 font-medium">
                            {new Date(matchingComponent.expectedCompletionDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      
                      {/* Dimensions */}
                      {matchingComponent.dimensions && Object.keys(matchingComponent.dimensions).length > 0 && (
                        <div>
                          <span className="text-gray-500">Dimensions:</span>
                          <div className="mt-1 grid grid-cols-2 gap-1">
                            {Object.entries(matchingComponent.dimensions).map(([key, value]) => (
                              <div key={key} className="bg-white px-2 py-1 rounded text-xs">
                                <span className="text-gray-500">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                <span className="ml-1 font-medium">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Features */}
                      {matchingComponent.features && matchingComponent.features.length > 0 && (
                        <div>
                          <span className="text-gray-500">Features:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {matchingComponent.features.map((feature, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">
                                {feature.label || feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Fabric */}
                      {matchingComponent.fabric && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Fabric:</span>
                          <span className="text-gray-800 font-medium">
                            {matchingComponent.fabric.description || matchingComponent.fabric}
                            {matchingComponent.fabric.imageCount && ` (${matchingComponent.fabric.imageCount} images)`}
                          </span>
                        </div>
                      )}
                      
                      {/* Budget */}
                      {matchingComponent.budget && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Component budget:</span>
                          <span className="text-gray-800 font-medium">${parseFloat(matchingComponent.budget).toLocaleString()}</span>
                        </div>
                      )}
                      
                      {/* Notes */}
                      {matchingComponent.notes && (
                        <div>
                          <span className="text-gray-500">Notes:</span>
                          <p className="mt-1 text-gray-700 text-xs bg-white p-2 rounded">{matchingComponent.notes}</p>
                        </div>
                      )}
                      
                      {/* Component Photos */}
                      {(() => {
                        const compPhotos = componentPhotos[matchingComponent.id] || [];
                        if (compPhotos.length === 0) return null;
                        
                        return (
                          <div>
                            <span className="text-gray-500">Photos ({compPhotos.length}):</span>
                            <div className="mt-1 grid grid-cols-3 gap-1">
                              {compPhotos.slice(0, 3).map((photo) => (
                                <div key={photo.id} className="relative group cursor-pointer">
                                  <img
                                    src={photo.url}
                                    alt={`${matchingComponent.name} photo`}
                                    className="w-full h-12 object-cover rounded border border-gray-300 group-hover:border-pink-400 transition-colors"
                                    onClick={() => window.open(photo.url, '_blank')}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {compPhotos.length > 3 && (
                              <p className="mt-1 text-xs text-gray-400 text-center">
                                +{compPhotos.length - 3} more photo{compPhotos.length - 3 !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <CalendarDaysIcon className="w-4 h-4 mr-2" />
                    {task.workflowType === 'quote' || task.requiresQuote ? 'Due date for quote' : 'Due date'}: {new Date(task.dueDate?.seconds ? task.dueDate.seconds * 1000 : task.dueDate).toLocaleDateString()}
                  </div>
                  {task.budget > 0 && (
                    <div className="flex items-center text-gray-500">
                      <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                      {formatCurrency(task.budget)}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <select
                    value={task.status || 'pending'}
                    onChange={(e) => handleUpdateTask(task.id, { status: e.target.value })}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-pink-500"
                  >
                    {task.directQuoteCompleted ? (
                      // Direct Order statuses - simpler options
                      <>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </>
                    ) : task.requiresQuote === false ? (
                      // Job Sheet statuses
                      <>
                        <option value="pending">Pending</option>
                        <option value="job_sheet_sent">Job Sheet Sent</option>
                        <option value="job_sheet_acknowledged">Acknowledged</option>
                        <option value="queries_pending">Queries Pending</option>
                        <option value="queries_resolved">Queries Resolved</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </>
                    ) : (
                      // Quote workflow statuses
                      <>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </>
                    )}
                  </select>

                  {task.directQuoteCompleted ? (
                    // Direct Order actions - show supplier info
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                        <BuildingStorefrontIcon className="w-3 h-3 mr-1" />
                        Direct Order
                      </span>
                    </div>
                  ) : task.requiresQuote === false ? (
                    // Job Sheet actions
                    <div className="flex items-center gap-2">
                      {task.jobSheetId ? (
                        <span className="inline-flex items-center px-3 py-1 bg-green-50 text-green-600 text-xs font-medium rounded">
                          <DocumentTextIcon className="w-3 h-3 mr-1" />
                          Job Sheet: {task.jobSheetId.slice(-6)}
                        </span>
                      ) : (
                        <button
                          onClick={() => setJobSheetTask(task)}
                          className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-600 text-xs font-medium rounded hover:bg-orange-100 transition-colors"
                        >
                          <DocumentTextIcon className="w-3 h-3 mr-1" />
                          Send Job Sheet
                        </button>
                      )}
                    </div>
                  ) : (
                    // Quote actions
                    <Link
                      to={`/projects/${id}/tasks/${task.id}/quotes`}
                      className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded hover:bg-blue-100 transition-colors"
                    >
                      <UserGroupIcon className="w-3 h-3 mr-1" />
                      Quotes ({task.quotesCount || 0})
                    </Link>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                {task.directQuoteCompleted ? (
                  // Direct Order workflow
                  <div>
                    <div className="mb-3">
                      <span className="text-xs text-gray-500 font-medium">Direct Order Details:</span>
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {task.directQuoteSupplier || 'No supplier details provided'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Direct order - no platform communication required
                      </span>
                      <span className="text-xs text-blue-600 font-medium">
                        Handled directly by PFI
                      </span>
                    </div>
                  </div>
                ) : task.requiresQuote === false ? (
                  // Job Sheet workflow
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">
                        {task.status === 'pending' ? 'Ready for job sheet' : 
                         task.status === 'job_sheet_sent' ? `Job sheet sent to ${getSupplierNameForTask(task)} - awaiting acknowledgment` :
                         task.status === 'job_sheet_acknowledged' ? `Job sheet acknowledged by ${getSupplierNameForTask(task)}` :
                         task.status === 'queries_pending' ? 'Supplier queries pending' :
                         task.status === 'queries_resolved' ? 'All queries resolved' :
                         task.status === 'quotes_received' ? 'Quotes received - awaiting review' :
                         task.status === 'quote_accepted' ? `Quote accepted - ${task.acceptedSupplierName || 'supplier assigned'}` :
                         'Task status: ' + task.status.replace('_', ' ')}
                      </span>
                      {task.status === 'job_sheet_sent' && (
                        <span className="inline-flex items-center px-2 py-1 bg-yellow-50 text-yellow-600 text-xs font-medium rounded-full">
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                          Awaiting Acknowledgment
                        </span>
                      )}
                    </div>
                    
                    {task.jobSheetSentAt && (
                      <div className="text-xs text-gray-500 mt-1">
                        Sent: {new Date(task.jobSheetSentAt.seconds * 1000).toLocaleDateString()}
                        {task.jobSheetAcknowledgedAt && (
                          <> • Acknowledged: {new Date(task.jobSheetAcknowledgedAt.seconds * 1000).toLocaleDateString()}</>
                        )}
                      </div>
                    )}

                    {/* Show accepted quote details */}
                    {task.status === 'quote_accepted' && task.acceptedSupplierName && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium text-green-800">
                              Awarded to {task.acceptedSupplierName}
                            </span>
                          </div>
                          {task.acceptedAmount && (
                            <span className="text-sm font-bold text-green-800">
                              ${task.acceptedAmount.toLocaleString()}
                            </span>
                          )}
                        </div>
                        {(task.acceptedTimelineDays || task.acceptedTimeline) && (
                          <div className="mt-1 text-xs text-green-700">
                            Timeline: {task.acceptedTimelineDays ? `${task.acceptedTimelineDays} days` : task.acceptedTimeline}
                          </div>
                        )}
                        {task.quoteAcceptedAt && (
                          <div className="mt-1 text-xs text-green-600">
                            Accepted: {new Date(task.quoteAcceptedAt.seconds ? task.quoteAcceptedAt.seconds * 1000 : task.quoteAcceptedAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // Quote workflow
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {task.invitedSuppliers?.length || 0} suppliers invited
                        </span>
                        {task.invitedSuppliers?.length > 0 && (
                          <button
                            onClick={() => handleClearAllInvitations(task.id)}
                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                            title="Clear all invitations"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      <button
                        className="text-xs text-pink-600 hover:text-pink-700 font-medium"
                        onClick={() => setAssigningTask(task)}
                      >
                        Invite to Quote
                      </button>
                    </div>
                  </>
                )}
                
                {/* Quote-specific sections - only show for quote workflow */}
                {task.requiresQuote !== false && !task.directQuoteCompleted && (
                  <>
                    {/* Send Invitations Button - only show if suppliers invited but not notified */}
                    {task.invitedSuppliers?.length > 0 && 
                     task.invitedSuppliers.some(s => !s.invitationSent) && (
                      <button
                        onClick={() => handleSendInvitations(task)}
                        className="mt-2 w-full flex items-center justify-center px-3 py-1.5 bg-pink-50 text-pink-600 text-xs font-medium rounded-lg hover:bg-pink-100 transition-colors"
                      >
                        <PaperAirplaneIcon className="w-3 h-3 mr-1.5" />
                        Send Invitations ({task.invitedSuppliers.filter(s => !s.invitationSent).length} pending)
                      </button>
                    )}
                    
                    {/* Show invited suppliers and their quote status */}
                    {task.invitedSuppliers?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {task.invitedSuppliers.slice(0, 5).map((supplier, index) => (
                          <div key={`${supplier.id}-${index}`} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 truncate flex-1">{supplier.name}</span>
                            <div className="flex items-center gap-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                supplier.quoteStatus === 'submitted' 
                                  ? 'bg-green-100 text-green-700'
                                  : supplier.quoteStatus === 'won'
                                  ? 'bg-blue-100 text-blue-700 font-semibold'
                                  : supplier.quoteStatus === 'lost'
                                  ? 'bg-gray-100 text-gray-500'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {supplier.quoteStatus === 'submitted' ? 'Quoted' : 
                                 supplier.quoteStatus === 'won' ? 'Winner' :
                                 supplier.quoteStatus === 'lost' ? 'Not Selected' :
                                 supplier.invitationSent ? 'Notified' : 'Pending'}
                              </span>
                              <button
                                onClick={() => handleRemoveInvitedSupplier(task.id, index)}
                                className="text-red-400 hover:text-red-600 p-0.5"
                                title="Remove supplier invitation"
                              >
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {task.invitedSuppliers.length > 3 && (
                          <div className="text-xs text-gray-400">
                            +{task.invitedSuppliers.length - 3} more
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show accepted quote details for quote workflow */}
                    {task.status === 'quote_accepted' && task.acceptedSupplierName && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium text-green-800">
                              Awarded to {task.acceptedSupplierName}
                            </span>
                          </div>
                          {task.acceptedAmount && (
                            <span className="text-sm font-bold text-green-800">
                              ${task.acceptedAmount.toLocaleString()}
                            </span>
                          )}
                        </div>
                        {(task.acceptedTimelineDays || task.acceptedTimeline) && (
                          <div className="mt-1 text-xs text-green-700">
                            Timeline: {task.acceptedTimelineDays ? `${task.acceptedTimelineDays} days` : task.acceptedTimeline}
                          </div>
                        )}
                        {task.quoteAcceptedAt && (
                          <div className="mt-1 text-xs text-green-600">
                            Accepted: {new Date(task.quoteAcceptedAt.seconds ? task.quoteAcceptedAt.seconds * 1000 : task.quoteAcceptedAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {tasks.length === 0 && (
            <div className="col-span-full text-center py-12">
              <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks yet</h3>
              <p className="text-gray-500">Start by creating your first project task using the "Add Task" button above</p>
            </div>
          )}
        </div>

        {/* Edit Task Modal */}
        {editingTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
               onClick={(e) => {
                 if (e.target === e.currentTarget) {
                   setEditingTask(null);
                 }
               }}>
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Task</h2>
                <button
                  onClick={() => setEditingTask(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Task Name</label>
                  <input
                    type="text"
                    value={editingTask.name || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter task name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editingTask.description || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Describe the task details, requirements, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget ($)</label>
                    <input
                      type="number"
                      value={editingTask.budget || ''}
                      onChange={(e) => setEditingTask({ ...editingTask, budget: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={editingTask.status || 'pending'}
                      onChange={(e) => setEditingTask({ ...editingTask, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="pending">Pending</option>
                      <option value="quotes_pending">Quotes Pending</option>
                      <option value="quotes_received">Quotes Received</option>
                      <option value="quote_accepted">Quote Accepted</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={editingTask.category || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    <option value="flooring">Flooring</option>
                    <option value="painting">Painting</option>
                    <option value="electrical">Electrical</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="carpentry">Carpentry</option>
                    <option value="upholstery">Upholstery</option>
                    <option value="lighting">Lighting</option>
                    <option value="furniture">Furniture</option>
                    <option value="décor">Décor</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={async () => {
                      try {
                        await updateDoc(doc(db, 'projects', id, 'tasks', editingTask.id), {
                          name: editingTask.name,
                          description: editingTask.description,
                          budget: editingTask.budget,
                          status: editingTask.status,
                          category: editingTask.category,
                          updatedAt: serverTimestamp()
                        });
                        
                        setEditingTask(null);
                        loadData(); // Reload tasks
                        alert('Task updated successfully!');
                      } catch (error) {
                        console.error('Error updating task:', error);
                        alert('Failed to update task');
                      }
                    }}
                    className="flex-1 bg-pink-600 text-white py-3 px-6 rounded-lg hover:bg-pink-700 transition-colors font-semibold"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditingTask(null)}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Supplier Assignment Modal */}
        {assigningTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
               onClick={(e) => {
                 if (e.target === e.currentTarget) {
                   setAssigningTask(null);
                 }
               }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Invite Suppliers to Quote: {assigningTask.name}
                </h2>
                
                <div className="space-y-3 mb-6">
                  {suppliers.length > 0 ? (
                    suppliers.map((supplier) => {
                      const isInvited = assigningTask.invitedSuppliers?.some(s => s.id === supplier.id) || false;
                      return (
                        <div key={supplier.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={isInvited}
                            onChange={(e) => {
                              const currentSuppliers = assigningTask.invitedSuppliers || [];
                              let updatedSuppliers;
                              
                              if (e.target.checked) {
                                updatedSuppliers = [...currentSuppliers, { ...supplier, status: 'invited' }];
                              } else {
                                updatedSuppliers = currentSuppliers.filter(s => s.id !== supplier.id);
                              }
                              
                              setAssigningTask({ ...assigningTask, invitedSuppliers: updatedSuppliers });
                            }}
                            className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{supplier.name}</div>
                            <div className="text-sm text-gray-500 capitalize">{supplier.category}</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BuildingStorefrontIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No suppliers available</p>
                      <p className="text-sm">Add suppliers first in the Suppliers page</p>
                    </div>
                  )}
                </div>

                {/* Send Immediately Option */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="sendImmediately"
                      checked={sendInvitationsImmediately}
                      onChange={(e) => setSendInvitationsImmediately(e.target.checked)}
                      className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <label htmlFor="sendImmediately" className="text-sm font-medium text-blue-900 cursor-pointer">
                      Send invitations immediately
                    </label>
                  </div>
                  <p className="text-xs text-blue-700 mt-1 ml-7">
                    {sendInvitationsImmediately 
                      ? "Suppliers will receive email and SMS notifications right away" 
                      : "Suppliers will be assigned but you'll need to send invitations manually later"
                    }
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      const supplierIds = (assigningTask.invitedSuppliers || []).map(s => s.id);
                      handleAssignSuppliers(assigningTask.id, supplierIds, sendInvitationsImmediately);
                    }}
                    className="flex-1 px-4 py-2 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    {sendInvitationsImmediately ? 'Assign & Send Invitations' : 'Assign Suppliers'}
                  </button>
                  <button
                    onClick={() => setAssigningTask(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Job Sheet Modal */}
        <JobSheetModal
          task={jobSheetTask}
          project={project}
          suppliers={suppliers}
          isOpen={!!jobSheetTask}
          onClose={() => setJobSheetTask(null)}
          onJobSheetSent={loadData}
        />

        {/* Supplier Invitation Prompt Modal */}
        <SupplierInvitationPromptModal
          isOpen={showSupplierPrompt}
          onClose={() => setShowSupplierPrompt(false)}
          task={createdTask}
          project={project}
          onInviteSuppliers={handleInviteSuppliers}
          onViewTask={handleViewTask}
        />

        {/* Quick Task Rating Modal */}
        <QuickTaskRatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            setTaskToRate(null);
          }}
          task={taskToRate}
          project={project}
          onRatingSubmitted={() => {
            loadData(); // Refresh to show rating
          }}
        />
      </div>
    </AppLayout>
  );
}